import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { type GetPlanActionItemDto, type TaskItemDto, type UserDto } from "src/http";
import type { ITeamSummary } from "src/models";
import { TeamAutocomplete } from "../TeamAutocomplete";
import { UserAutocomplete } from "../UserAutocomplete";
import { StrategicItemPicker } from "./StrategicItemPicker";
import { TaskItemPicker } from "./TaskItemPicker";

interface Props {
  open: boolean;
  onDone: (params: ReferencedItemAddedParams) => void;
  onClose: () => void;
}

interface ReferencedItemAddedParams {
  itemType: ReferencedWorkItemType;
  actionItem: GetPlanActionItemDto | null;
  actionItemTeam: ITeamSummary | null;
  taskItem: TaskItemDto | null;
  taskItemPerson: UserDto | null;
}

enum ReferencedWorkItemType {
  ActionItem = "actionItem",
  TaskItem = "taskItem",
}

interface FormValues {
  itemType: ReferencedWorkItemType;
  actionItemTeam: ITeamSummary | null;
  actionItem: GetPlanActionItemDto | null;
  taskItemPerson: UserDto | null;
  taskItem: TaskItemDto | null;
}

const AddReferenceWorkModal = (props: Props) => {
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      itemType: ReferencedWorkItemType.ActionItem,
    },
  });

  const itemTypeValue = useWatch({ control, name: "itemType" });
  const actionItemTeam = useWatch({ control, name: "actionItemTeam" });
  const taskItemPerson = useWatch({ control, name: "taskItemPerson" });

  function handleDoneClicked() {
    handleSubmit((formState) => {
      reset(undefined, { keepDefaultValues: true });

      props.onDone({
        itemType: formState.itemType,
        actionItem: formState.actionItem,
        actionItemTeam: formState.actionItemTeam,
        taskItem: formState.taskItem,
        taskItemPerson: formState.taskItemPerson,
      });
    })();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onClose();
  }

  return (
    <Dialog open={props.open} onClose={handleCancelClicked}>
      <DialogTitle>Add Reference Work</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={1}>
          <Controller
            control={control}
            name="itemType"
            render={({ field }) => (
              <RadioGroup {...field}>
                <FormControlLabel
                  value={ReferencedWorkItemType.ActionItem}
                  control={<Radio />}
                  label="Add Strategic Item"
                />
                <FormControlLabel value={ReferencedWorkItemType.TaskItem} control={<Radio />} label="Add Task" />
              </RadioGroup>
            )}
          />

          <Divider variant="dashed" />

          {itemTypeValue === ReferencedWorkItemType.ActionItem && (
            <>
              <Stack>
                <Typography variant="label">Team</Typography>
                <Controller
                  control={control}
                  name="actionItemTeam"
                  render={({ field }) => (
                    <TeamAutocomplete value={field.value ?? null} onChange={(value) => field.onChange(value)} />
                  )}
                />
              </Stack>
              <Stack>
                <Typography variant="label">Strategic Item</Typography>
                <Controller
                  control={control}
                  name="actionItem"
                  render={({ field }) => (
                    <StrategicItemPicker teamSlug={actionItemTeam?.slug} onChange={(value) => field.onChange(value)} />
                  )}
                />
              </Stack>
            </>
          )}

          {itemTypeValue === ReferencedWorkItemType.TaskItem && (
            <>
              <Stack>
                <Typography variant="label">Person</Typography>
                <Controller
                  control={control}
                  name="taskItemPerson"
                  render={({ field }) => (
                    <UserAutocomplete value={field.value ?? null} onChange={(value) => field.onChange(value)} />
                  )}
                />
              </Stack>
              <Stack>
                <Typography variant="label">Task</Typography>
                <Controller
                  control={control}
                  name="taskItem"
                  render={({ field }) => (
                    <TaskItemPicker userId={taskItemPerson?.userId} onChange={(value) => field.onChange(value)} />
                  )}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleDoneClicked}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AddReferenceWorkModal, ReferencedWorkItemType, type ReferencedItemAddedParams };
